import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { OrgTemplate } from '@newgenus/common';
import {
    CloneableOrgSummary,
    OrgTemplateService,
    PickerTemplate,
    TemplateSourceTag,
} from '../services/org-template.service';

/**
 * Combined picker for the wizard's Step 1 "Curated template" and
 * "Clone an org I admin" cards (plan P4.a + P4.b).
 *
 * One dialog, two modes — chosen by the caller. Renders a uniform list with
 * name / sub-label / part-count, and returns the picked entity so the caller
 * can hydrate the draft.
 *
 * Modes:
 *  - `'template'` — queries `OrgTemplateService.listSystemTemplates()`.
 *  - `'clone'`    — queries `OrgTemplateService.loadCloneableOrgs()`.
 */
export type OrgSourcePickerMode = 'template' | 'clone';

export interface OrgSourcePickerData {
    mode: OrgSourcePickerMode;
}

export type OrgSourcePickerResult =
    | { kind: 'template'; template: OrgTemplate }
    | { kind: 'clone'; org: CloneableOrgSummary }
    | { kind: 'cancel' };

interface PickerRow {
    title: string;
    sub: string;
    partCount: number;
    /** Set for template-mode rows; drives the small provenance chip in the picker. */
    sourceTag?: TemplateSourceTag;
    sourceLabel?: string;
    /** Discriminator + payload — `template` xor `org` is set. */
    template?: OrgTemplate;
    org?: CloneableOrgSummary;
}

@Component({
    selector: 'newgenus-org-source-picker-dialog',
    templateUrl: './org-source-picker-dialog.component.html',
    styleUrls: ['./org-source-picker-dialog.component.scss'],
    standalone: false,
})
export class OrgSourcePickerDialogComponent implements OnInit {
    public mode: OrgSourcePickerMode;
    public rows: PickerRow[] = [];
    public loading = true;
    public errorMessage = '';

    constructor(
        public dialogRef: MatDialogRef<OrgSourcePickerDialogComponent, OrgSourcePickerResult>,
        @Inject(MAT_DIALOG_DATA) data: OrgSourcePickerData,
        private templates: OrgTemplateService
    ) {
        this.mode = data.mode;
    }

    public async ngOnInit(): Promise<void> {
        try {
            if (this.mode === 'template') {
                const items = await this.templates.loadAvailableTemplates();
                this.rows = items.map((entry) => this.rowFromTemplate(entry));
            } else {
                const orgs = await this.templates.loadCloneableOrgs();
                this.rows = orgs.map((o) => ({
                    title: o.name,
                    sub: o.modifiedMs ? `Updated ${this.relativeDate(o.modifiedMs)}` : '—',
                    partCount: o.partCount,
                    org: o,
                }));
            }
        } catch (err: unknown) {
            this.errorMessage = (err instanceof Error ? err.message : String(err)) || 'Failed to load list.';
        } finally {
            this.loading = false;
        }
    }

    private rowFromTemplate(entry: PickerTemplate): PickerRow {
        const t = entry.template;
        let label = '';
        switch (entry.sourceTag) {
            case 'system':
                label = 'System';
                break;
            case 'shared':
                label = 'Shared';
                break;
            case 'org-private':
                label = entry.ownerOrgName ? `From ${entry.ownerOrgName}` : 'From your org';
                break;
        }
        return {
            title: t.name,
            sub: t.description || '—',
            partCount: Array.isArray(t.snapshot?.parts) ? t.snapshot.parts.length : 0,
            sourceTag: entry.sourceTag,
            sourceLabel: label,
            template: t,
        };
    }

    public get titleText(): string {
        return this.mode === 'template' ? 'Pick a curated template' : 'Clone an organization';
    }

    public get emptyText(): string {
        return this.mode === 'template'
            ? 'No curated templates are available yet. Ask an admin to seed system templates.'
            : 'No organizations available to clone — you need to be a member of at least one org.';
    }

    public pick(row: PickerRow): void {
        if (row.template) {
            this.dialogRef.close({ kind: 'template', template: row.template });
        } else if (row.org) {
            this.dialogRef.close({ kind: 'clone', org: row.org });
        }
    }

    public cancel(): void {
        this.dialogRef.close({ kind: 'cancel' });
    }

    private relativeDate(ms: number): string {
        const diff = Date.now() - ms;
        const day = 24 * 60 * 60 * 1000;
        if (diff < day) return 'today';
        if (diff < 2 * day) return 'yesterday';
        if (diff < 7 * day) return `${Math.floor(diff / day)} days ago`;
        const weeks = Math.floor(diff / (7 * day));
        if (weeks < 5) return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
        const months = Math.floor(diff / (30 * day));
        return `${months} month${months > 1 ? 's' : ''} ago`;
    }
}
